import { createAsyncAction, createReducer, ActionType, createAction } from 'typesafe-actions';
import { FetchAction, SuccessFetchActionPayload, DefaultErrorPayload } from '../dataFetching/generators';
import { DataFetchingThunkAction } from '../types';
import { log404ErrorAsWarning } from '../../../common/apiAdapters/ErrorHandlers';
import { addNotification } from '../notifications/actions';
import { DisplayType } from '../notifications/types';
import { OverlayId } from '../../components/Overlays/types';
import { hideOverlay } from '../overlays/actions';
import { GenericShopApiResponse } from '../../../common/apiAdapters/types';
import { get } from '../../../common/utils/objectUtils';
import { PDPApiProductData } from './types';

export interface FabricSampleState {
    isFetching: boolean;
    success: boolean;
    validationErrors: FabricSampleAddressFields;
}

/* eslint-disable @typescript-eslint/camelcase, camelcase */
export interface ApiFabricSampleOrder {
    samples: string;
    full_name: string;
    address_line_one: string;
    address_line_two?: string;
    email: string;
    postcode: string;
    city: string;
    country: string;
}

interface FabricSampleApiError {
    fields?: string[];
    message: string;
}

/* eslint-enable @typescript-eslint/camelcase, camelcase */
export type FabricSampleOrderResponse = GenericShopApiResponse<{ message: string }, FabricSampleApiError[]>;

export interface FabricSampleAddressFields {
    name?: string;
    street?: string;
    additionalAddress?: string;
    email?: string;
    postcode?: string;
    city?: string;
    country?: string;
}

export interface FabricSampleCountry {
    code: string;
    poKey: string;
}

export interface FabricSampleErrorPayload extends DefaultErrorPayload {
    validationErrors: FabricSampleAddressFields;
}

export const orderFabricSampleActions = createAsyncAction(
    '@PRODUCT_DETAILS_FABRIC_SAMPLE/POST_START',
    '@PRODUCT_DETAILS_FABRIC_SAMPLE/POST_SUCCESS',
    '@PRODUCT_DETAILS_FABRIC_SAMPLE/POST_FAILED'
)<FetchAction, SuccessFetchActionPayload<FabricSampleOrderResponse>, FabricSampleErrorPayload>();

export const resetFabricSample = createAction('@PRODUCT_DETAILS_FABRIC_SAMPLE/RESET');

export const OrderFabricSampleAllActions = [
    orderFabricSampleActions.request,
    orderFabricSampleActions.success,
    orderFabricSampleActions.failure,
    resetFabricSample,
];

export const initialState = {};

export type OrderFabricSampleActionsType =
    | ActionType<typeof orderFabricSampleActions.request>
    | ActionType<typeof orderFabricSampleActions.success>
    | ActionType<typeof orderFabricSampleActions.failure>
    | ActionType<typeof resetFabricSample>;

const defaultState = {
    isFetching: false,
    success: false,
    validationErrors: {},
};

export const orderFabricSampleReducer = createReducer<FabricSampleState, OrderFabricSampleActionsType>(defaultState)
    .handleAction(resetFabricSample, () => defaultState)
    .handleAction(orderFabricSampleActions.request, () => ({
        isFetching: true,
        success: false,
        validationErrors: {},
    }))
    .handleAction(orderFabricSampleActions.success, () => ({
        isFetching: false,
        success: true,
        validationErrors: {},
    }))
    .handleAction(orderFabricSampleActions.failure, (_, { payload: { validationErrors } }) => ({
        isFetching: false,
        success: false,
        validationErrors,
    }));

export const buildOrderFabricSampleBody = (
    skus: string[],
    addressData: FabricSampleAddressFields
): ApiFabricSampleOrder => {
    return {
        samples: skus.join(','),
        full_name: addressData.name || '',
        email: addressData.email || '',
        address_line_one: addressData.street || '',
        address_line_two: addressData.additionalAddress || ' ',
        postcode: addressData.postcode || '',
        city: addressData.city || '',
        country: addressData.country || '',
    };
};

// build proper form validation error object from api error fields
export const buildFormValidationErrorsFromApi = (errors: string[], errorMessage: string): FabricSampleAddressFields => {
    const mapping = {
        full_name: 'name',
        address_line_one: 'street',
        address_line_two: 'additionalAddress',
    };
    return errors.reduce((errorsObject, field) => ({ ...errorsObject, [mapping[field] || field]: errorMessage }), {});
};

export const orderFabricSample = (
    simpleSkus: string[],
    addressData: FabricSampleAddressFields,
    errorNotification: string
): DataFetchingThunkAction => async (dispatch, _, { apiService, logger }) => {
    const identifier = simpleSkus.join(',');

    if (!identifier) {
        return;
    }
    dispatch(orderFabricSampleActions.request({ identifier }));
    try {
        const result = await apiService.shopApi.orderFabricSample(buildOrderFabricSampleBody(simpleSkus, addressData));
        if (result.success) {
            dispatch(orderFabricSampleActions.success({ identifier, data: result }));
        } else {
            const error = result.errors?.[0]?.message || '';
            const formFieldError = result.errors?.[0]?.fields || [];
            dispatch(
                orderFabricSampleActions.failure({
                    error,
                    validationErrors: buildFormValidationErrorsFromApi(formFieldError, error),
                    identifier,
                })
            );
        }
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'orderFabricSample');
        dispatch(orderFabricSampleActions.failure({ error: err, identifier, validationErrors: {} }));
        dispatch(addNotification(errorNotification, null, DisplayType.error));
        dispatch(hideOverlay(OverlayId.FABRIC_SAMPLE_OVERLAY));
    }
};

export const getFabricSampleCountries = (pdpData: PDPApiProductData): FabricSampleCountry[] => {
    // eslint-disable-next-line camelcase
    return get(pdpData, 'meta.fabric_sample_countries', []).map((elm: { code: string; po_key: string }) => ({
        code: elm.code,
        poKey: elm.po_key,
    }));
};
